<script setup lang="ts">
/** Контекст упоминания: несколько сообщений до и после, упоминание подсвечено; переход в Telegram. */
import { computed, nextTick, ref, watch } from 'vue';
import type { ChatMessageDto, MentionDto } from '@sync-tracker/shared';
import { api } from '@/api';
import { formatDateTime, formatTime } from '@/lib/format';
import { Icon, UiAvatar, UiButton, UiModal, UiSpinner } from '@/ui';

const props = defineProps<{ mention: MentionDto | null; username: string | null }>();
const emit = defineEmits<{ close: []; dismiss: [m: MentionDto] }>();

const open = computed({
  get: () => !!props.mention,
  set: (v) => {
    if (!v) emit('close');
  },
});

const messages = ref<ChatMessageDto[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const bodyEl = ref<HTMLElement | null>(null);

watch(
  () => props.mention,
  async (m) => {
    messages.value = [];
    error.value = null;
    if (!m) return;
    loading.value = true;
    try {
      messages.value = (await api.tickets.context(m.chatId, m.messageId)).messages;
      await nextTick();
      bodyEl.value?.querySelector('.msg.target')?.scrollIntoView({ block: 'center' });
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
    } finally {
      loading.value = false;
    }
  },
);

function parts(text: string): { text: string; me: boolean }[] {
  const u = props.username;
  if (!u) return [{ text, me: false }];
  const re = new RegExp(`(@${u})(?![A-Za-z0-9_])`, 'gi');
  return text.split(re).filter(Boolean).map((t) => ({ text: t, me: t.toLowerCase() === `@${u}`.toLowerCase() }));
}

function dismiss() {
  if (!props.mention) return;
  emit('dismiss', props.mention);
  emit('close');
}
</script>

<template>
  <UiModal v-model="open" :title="mention?.chatTitle ?? ''" :subtitle="mention ? `Упоминание · ${formatDateTime(mention.date)}` : ''" :width="560">
    <div ref="bodyEl" class="ctx">
      <div v-if="loading" class="center"><UiSpinner label="Загружаю переписку…" /></div>
      <p v-else-if="error" class="small danger">{{ error }}</p>
      <template v-else>
        <div
          v-for="m in messages"
          :key="m.messageId"
          class="msg"
          :class="{ staff: m.isEmployee, target: m.messageId === mention?.messageId }"
        >
          <UiAvatar v-if="!m.isEmployee" :name="m.fromName ?? m.fromUsername" :size="24" />
          <div class="bubble">
            <div class="msg-head">
              <span class="msg-name">{{ m.fromName ?? m.fromUsername ?? '—' }}</span>
              <span v-if="m.isEmployee" class="staff-tag">BS</span>
              <span class="msg-time">{{ formatTime(m.date, true) }}</span>
            </div>
            <div class="msg-text">
              <span v-if="m.mediaType" class="media"><Icon name="paperclip" :size="12" /> {{ m.mediaType }}</span>
              <template v-for="(p, i) in parts(m.text ?? '')" :key="i"><mark v-if="p.me">{{ p.text }}</mark><template v-else>{{ p.text }}</template></template>
            </div>
          </div>
        </div>
        <div v-if="!messages.length" class="center faint small">Сообщения недоступны</div>
      </template>
    </div>

    <template #footer>
      <UiButton variant="ghost" icon="eyeOff" @click="dismiss">Скрыть упоминание</UiButton>
      <span class="grow" />
      <span v-if="mention && !mention.deepLink" class="faint small">Ссылка есть только у супергрупп</span>
      <a v-if="mention?.deepLink" :href="mention.deepLink" target="_blank" rel="noopener" class="tg-link">
        <UiButton variant="primary" icon="external">Открыть в Telegram</UiButton>
      </a>
    </template>
  </UiModal>
</template>

<style scoped>
.ctx {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 56vh;
  overflow: auto;
  padding: 2px;
}
.center {
  display: grid;
  place-items: center;
  padding: 30px 0;
}
.msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 88%;
}
.msg.staff {
  align-self: flex-end;
}
.bubble {
  padding: 7px 10px;
  border-radius: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  min-width: 0;
}
.msg.staff .bubble {
  background: color-mix(in srgb, var(--accent) 10%, var(--surface));
  border-color: color-mix(in srgb, var(--accent) 30%, var(--border));
}
.msg.target .bubble {
  border-color: var(--warning);
  box-shadow: 0 0 0 3px var(--warning-soft);
}
.msg-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}
.msg-name {
  font-weight: 600;
}
.staff-tag {
  font-size: 10px;
  padding: 0 5px;
  border-radius: 4px;
  background: var(--accent);
  color: #fff;
}
.msg-time {
  color: var(--faint);
  font-family: var(--font-mono, monospace);
  font-size: 11px;
}
.msg-text {
  margin-top: 2px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13.5px;
}
.media {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--muted);
  font-size: 12px;
  margin-right: 4px;
}
mark {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent);
  border-radius: 4px;
  padding: 0 2px;
  font-weight: 600;
}
.tg-link {
  text-decoration: none;
}
</style>
